import React from 'react';
import { Video, Globe, ArrowRight, Zap, Target } from 'lucide-react';
import { Button } from './Button';
import { Link } from './Router';

export const Features: React.FC = () => {
  return (
    <section id="services" className="py-32 bg-white relative overflow-hidden">
      {/* Massive Gradient Background */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[120%] h-[120%] bg-gradient-to-b from-transparent via-orange-50/50 to-transparent pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* HEADER SECTION - STRICTLY CENTERED */}
        <div className="flex flex-col items-center justify-center text-center w-full mb-20 mx-auto">
           
           {/* Icon */}
           <div className="inline-flex items-center justify-center p-4 bg-gradient-to-br from-brand-orange to-red-600 rounded-2xl mb-8 shadow-xl shadow-orange-500/40 rotate-3 animate-fade-in-up opacity-0">
              <Zap className="text-white w-10 h-10 fill-current" />
           </div>
           
           {/* Headline */}
           <h2 className="text-5xl md:text-8xl font-black text-brand-dark uppercase tracking-tighter leading-none mb-8 drop-shadow-sm w-full text-center animate-fade-in-up-delay-1 opacity-0">
             Money Making <br className="hidden md:block" /><span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-orange to-red-600">Creatives.</span>
           </h2>
           
           {/* Updated Text */}
           <div className="bg-brand-orange/5 border border-brand-orange/10 p-6 rounded-2xl max-w-3xl mx-auto mb-10 w-full animate-fade-in-up-delay-2 opacity-0">
             <p className="text-xl md:text-2xl font-bold text-brand-dark leading-relaxed text-center">
               Wir machen keine langwierigen Potenzialanalysen. Wir melden uns direkt mit einem Angebot & Strategie.
             </p>
           </div>

           {/* Button */}
           <div className="w-full flex justify-center items-center text-center animate-fade-in-up-delay-3 opacity-0">
                <Link to="/kontakt" className="inline-block">
                    <Button variant="outline" className="text-xl px-12 py-5 border-2 hover:bg-brand-dark hover:border-brand-dark hover:text-white transition-all shadow-lg hover:shadow-xl mx-auto flex items-center">
                        <Target className="mr-3" />
                        Strategie-Gespräch vereinbaren
                    </Button>
                </Link>
           </div>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          
          {/* Card 1: Content Production */}
          <div className="group relative bg-[#111111] rounded-[3rem] p-10 md:p-14 overflow-hidden min-h-[600px] flex flex-col justify-between hover:shadow-2xl hover:shadow-black/40 transition-all duration-700 ease-apple border border-gray-800 animate-fade-in-up opacity-0" style={{ animationDelay: '0.4s' }}>
            <Link to="/leistungen" className="absolute inset-0 z-20" aria-label="Mehr zu Content Produktion" />
            
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-brand-orange/30 to-transparent blur-[100px] rounded-full group-hover:bg-brand-orange/40 transition-colors duration-700"></div>
            
            <div className="relative z-10 transform transition-transform duration-700 ease-apple group-hover:-translate-y-2 pointer-events-none">
               <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mb-10 border border-white/10 backdrop-blur-md group-hover:scale-110 transition-transform duration-500 ease-apple shadow-inner">
                 <Video className="text-brand-orange w-10 h-10" />
               </div>
               <h3 className="text-5xl md:text-6xl font-black text-white mb-8 uppercase tracking-tight leading-[0.9]">Content<br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-gray-200 to-gray-500">Produktion</span></h3>
               <p className="text-gray-400 text-xl font-medium leading-relaxed max-w-md">
                 Wir ballern Reels und TikToks raus, die nicht nur Views bringen, sondern Kunden. High-Speed Execution. Kein langweiliges Corporate-Gelaber. Wir treffen den Nerv der Zeit.
               </p>
            </div>
            
            <div className="mt-12 pt-10 border-t border-white/10 flex items-center gap-6 text-white font-bold group-hover:text-brand-orange transition-colors duration-300 relative z-10 pointer-events-none">
               <span className="uppercase tracking-widest text-sm">Mehr erfahren</span>
               <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-brand-orange group-hover:text-white transition-all duration-500 ease-apple transform group-hover:rotate-45">
                  <ArrowRight size={20} />
               </div>
            </div>
          </div>

          {/* Card 2: Website Erstellung */}
          <div className="group relative bg-white border-2 border-gray-100 rounded-[3rem] p-10 md:p-14 overflow-hidden min-h-[600px] flex flex-col justify-between hover:border-brand-orange hover:shadow-2xl hover:shadow-orange-100 transition-all duration-700 ease-apple animate-fade-in-up opacity-0" style={{ animationDelay: '0.6s' }}>
            <Link to="/leistungen" className="absolute inset-0 z-20" aria-label="Mehr zu Website Erstellung" />

            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-gradient-to-tr from-gray-100 to-transparent blur-[100px] rounded-full"></div>
            
            <div className="relative z-10 transform transition-transform duration-700 ease-apple group-hover:-translate-y-2 pointer-events-none">
               <div className="w-20 h-20 bg-gradient-to-br from-brand-orange/10 to-orange-100 rounded-2xl flex items-center justify-center mb-10 border border-brand-orange/20 group-hover:scale-110 transition-transform duration-500 ease-apple">
                 <Globe className="text-brand-orange w-10 h-10" />
               </div>
               <h3 className="text-5xl md:text-6xl font-black text-brand-dark mb-8 uppercase tracking-tight leading-[0.9]">Website<br/><span className="text-brand-orange">Erstellung</span></h3>
               <p className="text-gray-600 text-xl font-medium leading-relaxed max-w-md">
                 Keine digitalen Visitenkarten, sondern Verkaufsmaschinen. Schnelle Ladezeiten, brutale Conversion-Funnels und SEO, das knallt. Wir verwandeln Traffic in Anmeldungen.
               </p>
            </div>
            
            <div className="mt-12 pt-10 border-t border-gray-100 flex items-center gap-6 text-brand-dark font-bold group-hover:text-brand-orange transition-colors duration-300 relative z-10 pointer-events-none">
               <span className="uppercase tracking-widest text-sm">Mehr erfahren</span>
               <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center group-hover:bg-brand-orange group-hover:text-white transition-all duration-500 ease-apple transform group-hover:rotate-45">
                  <ArrowRight size={20} />
               </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};